import { forceCacheFaceModels, forceCacheOnnxModels, precacheAttendancePages } from '@/lib/serviceWorkerRegistration';
import {
  loadFaceApiModels,
  preloadFaceApiLibrary,
} from '@/components/attendance/services/faceApiLoader';

type OfflinePreparationUpdateStatus = 'loading' | 'success' | 'error';

export interface PrepareAttendanceOfflineAssetsOptions {
  pageStepIndex: number;
  modelStepIndex: number;
  updateProgress: (
    step: string,
    index: number,
    status: OfflinePreparationUpdateStatus,
    count?: number,
  ) => void;
  logPrefix?: string;
}

export interface PrepareAttendanceOfflineAssetsResult {
  pageCacheResult: {
    success: boolean;
    cached?: number;
    total?: number;
    error?: string;
  };
  modelCacheResult: {
    success: boolean;
    ready: boolean;
    mediaPipe: { cached: number; failed: number; total: number };
    faceApi: { cached: number; failed: number; total: number };
    error?: string;
  };
  libraryLoaded: boolean;
  modelsFullyLoaded: boolean;
  modelsReady: boolean;
  totalModelsCached: number;
  error?: string;
}

export async function prepareAttendanceOfflineAssets({
  pageStepIndex,
  modelStepIndex,
  updateProgress,
  logPrefix = '[Offline Prep]',
}: PrepareAttendanceOfflineAssetsOptions): Promise<PrepareAttendanceOfflineAssetsResult> {
  const serializeForLog = (value: unknown): string => {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  };

  updateProgress('Pre-caching app pages...', pageStepIndex, 'loading');
  const pageCacheResult = await precacheAttendancePages();
  updateProgress(
    'App pages cached',
    pageStepIndex,
    pageCacheResult.success ? 'success' : 'error',
    pageCacheResult.cached,
  );

  updateProgress('Caching face recognition models...', modelStepIndex, 'loading');

  let libraryLoaded = false;
  try {
    console.log(`${logPrefix} Pre-loading face-api.js library...`);
    libraryLoaded = await preloadFaceApiLibrary();
    console.log(`${logPrefix} Face-api library preload result:`, libraryLoaded);
  } catch (error) {
    console.error(`${logPrefix} Face-api library preload failed:`, error);
  }

  console.log(`${logPrefix} Force caching face recognition model files...`);
  const initialModelCacheResult = await forceCacheFaceModels();
  console.log(`${logPrefix} Initial face model cache result: ${serializeForLog(initialModelCacheResult)}`);

  let modelsFullyLoaded = false;
  try {
    console.log(`${logPrefix} Loading face-api models to ensure chunk caching...`);
    modelsFullyLoaded = await loadFaceApiModels();
    console.log(`${logPrefix} Face-api models fully loaded:`, modelsFullyLoaded);
  } catch (error) {
    console.error(`${logPrefix} Face-api model loading failed:`, error);
  }

  console.log(`${logPrefix} Re-validating cached face model files...`);
  const finalModelCacheResult = await forceCacheFaceModels();
  console.log(`${logPrefix} Final face model cache result: ${serializeForLog(finalModelCacheResult)}`);

  // Cache TFLite Web Runtime + DocAligner TFLite model for offline document edge detection.
  // Non-fatal: if this fails, the camera falls back to canvas-based edge detection.
  console.log(`${logPrefix} Caching TFLite document detection models...`);
  try {
    const tfliteCacheResult = await forceCacheOnnxModels();
    console.log(`${logPrefix} TFLite model cache result: ${serializeForLog(tfliteCacheResult)}`);
  } catch (tfliteErr) {
    console.warn(`${logPrefix} TFLite model caching failed (non-fatal):`, tfliteErr);
  }

  const totalModelsCached =
    (finalModelCacheResult.mediaPipe?.cached || 0)
    + (finalModelCacheResult.faceApi?.cached || 0);
  const mediaPipeTotal = Number(finalModelCacheResult.mediaPipe?.total || 0);
  const faceApiTotal = Number(finalModelCacheResult.faceApi?.total || 0);
  const mediaPipeCached = Number(finalModelCacheResult.mediaPipe?.cached || 0);
  const faceApiCached = Number(finalModelCacheResult.faceApi?.cached || 0);
  const enoughModelsCached = (
    (mediaPipeTotal === 0 || mediaPipeCached >= Math.min(mediaPipeTotal, 2))
    && (faceApiTotal === 0 || faceApiCached >= Math.min(faceApiTotal, 6))
  );
  const cacheReportedReady = Boolean(finalModelCacheResult.ready || finalModelCacheResult.success);
  const modelsReady = (libraryLoaded || modelsFullyLoaded)
    && (cacheReportedReady || enoughModelsCached);

  console.log(`${logPrefix} Face model readiness evaluation: ${serializeForLog({
    libraryLoaded,
    modelsFullyLoaded,
    cacheReportedReady,
    enoughModelsCached,
    modelsReady,
    totals: { mediaPipeTotal, faceApiTotal },
    cached: { mediaPipeCached, faceApiCached },
  })}`);

  updateProgress(
    'Face recognition models ready',
    modelStepIndex,
    modelsReady ? 'success' : 'error',
    totalModelsCached,
  );

  const error = !pageCacheResult.success
    ? pageCacheResult.error || 'Failed to cache attendance pages'
    : !modelsReady
      ? finalModelCacheResult.error
        || initialModelCacheResult.error
        || 'Face models not ready for offline use'
      : undefined;

  if (error) {
    console.error(`${logPrefix} Attendance offline assets preparation error: ${serializeForLog({
      error,
      pageCacheResult,
      initialModelCacheResult,
      finalModelCacheResult,
      libraryLoaded,
      modelsFullyLoaded,
      modelsReady,
    })}`);
  }

  return {
    pageCacheResult,
    modelCacheResult: finalModelCacheResult,
    libraryLoaded,
    modelsFullyLoaded,
    modelsReady,
    totalModelsCached,
    error,
  };
}

export default prepareAttendanceOfflineAssets;
